import { SentryBreadcrumb } from '@common/sentry/SentryBreadcrumb';
import { Entity } from '@common/types/Entity';
import { EntityEvents } from '@common/user-analytics';
import { UserTypes } from '@common/UserTypes';
import { event as fullStoryEvent, identify, init as FullStoryInit, setUserVars } from '@fullstory/browser';
import { usePersistentCallback } from '@prophecy/utils/react/hooks';
import { addBreadcrumb, setUser } from '@sentry/react';
import mixpanel from 'mixpanel-browser';
import { nanoid } from 'nanoid';
import { useMemo } from 'react';
import { useImmer } from 'use-immer';

import { csrf } from '../../utils/csrf';
import { replayToken } from '../../utils/replayToken';
import { shareToken } from '../../utils/shareToken';
import { useAppMetadata } from '../appMetadata';
import { useGlobalVariables } from '../globalConfig/securedConfigHook';
import { MixPanelContext, MixPanelContextKeys, MixpanelStateType } from './types';
import {
  FullStoryAllowedDomains,
  fullStoryNameSpace,
  fullStoryOrgID,
  getCurrentTimeStamp,
  getMixpanelApIhost,
  getNameFromEmail,
  getSessionValue,
  MIXPANEL___TOKEN,
  MIXPANEL_ALLOWED_HOSTS
} from './util';

export const useMixPanelContext = () => {
  const appData = useAppMetadata();
  //Current user data is active user data whose project we are opening like share or normal project (owner of the active project and not the owner of active session)
  const currentUserData = appData?.user;
  //Logged in user data is the user data who is actually logged in using his/her credentials (owner of the active session)
  //Case when user has opened the replay or share project and logged in with some credentials then fetch loggedin user data
  const loggedInUserData = appData?.loggedInUserData;
  const isMixPanelEnabled = useGlobalVariables().isMixPanelEnabled;
  const [state, setState] = useImmer<MixpanelStateType>({
    enabled: false,
    isUserIdentified: false,
    isMixPanelLoaded: false,
    contexts: {}
  });

  const shareTokenValue = shareToken.get();

  const replayTokenValue = replayToken.get();
  const loggedInUserId = shareToken.loggedInUserId ?? replayToken.loggedInUserID;

  const hasSharedReplayProjectToken = shareTokenValue || replayTokenValue;

  const hasNonLoggedInGuestUserWithShareProject = !loggedInUserId && hasSharedReplayProjectToken;

  const getUserDetails = (email?: string, firstName?: string, lastName?: string) => {
    const tab_session_id = getSessionValue();
    let userEmail = currentUserData?.email ?? email ?? 'Public_User_' + tab_session_id;

    let userId = currentUserData?.id;
    let userFirstName = firstName;
    let userLastName = lastName;
    //Case when user has opened the replay or share project and not logged in
    if (hasNonLoggedInGuestUserWithShareProject) {
      userEmail = 'Public_Guest_User_Share_Project_' + tab_session_id;
      userLastName = '';
      userFirstName = userEmail;
      userId = userFirstName;
      //Case when user has opened the replay or share project and logged in with some credentials
    } else if (loggedInUserId && hasSharedReplayProjectToken) {
      userEmail = loggedInUserData?.email ?? '';
      userId = loggedInUserId;
      userFirstName = loggedInUserData?.firstName;
      userLastName = loggedInUserData?.lastName;
    }
    return { userEmail, userId, userFirstName, userLastName };
  };
  const isMixpanelAllowedHost = typeof window !== 'undefined' && MIXPANEL_ALLOWED_HOSTS.includes(window.location.host);

  const initMixpanel = usePersistentCallback((email?: string) => {
    const { userEmail } = getUserDetails(email);
    setState((draft) => {
      draft.isMixPanelLoaded = true;
      draft.enabled = true;
    });
    mixpanel.init(MIXPANEL___TOKEN, {
      api_host: getMixpanelApIhost(),
      ignore_dnt: true,
      // On allowed hosts: use cookies scoped to FQDN only.
      // On all other hosts (private/dedicated SaaS): use localStorage, no cookies.
      ...(isMixpanelAllowedHost
        ? { cross_subdomain_cookie: false, cookie_domain: window.location.hostname }
        : { persistence: 'localStorage' as const, disable_cookie: true })
    });
    mixpanel.set_config({
      xhr_headers: {
        [csrf.key]: csrf.get(),
        ...(shareTokenValue && { [shareToken.key]: shareTokenValue }),
        ...(replayTokenValue && { [replayToken.key]: replayTokenValue })
      }
    });
    mixpanel.register({ session_id: getSessionValue(), user_type: userEmail });
  });

  const loadFullStory = usePersistentCallback(async () => {
    const enabled = FullStoryAllowedDomains.includes(window.location.host);

    FullStoryInit({
      orgId: fullStoryOrgID,
      namespace: fullStoryNameSpace,
      devMode: !enabled
    });
  });

  const addContext = usePersistentCallback(<T extends keyof MixPanelContext>(key: T, value: MixPanelContext[T]) => {
    setState((draft) => {
      draft.contexts[key] = value;
    });
  });

  const removeContext = usePersistentCallback((key: MixPanelContextKeys) => {
    setState((draft) => {
      delete draft.contexts[key];
    });
  });

  const privateMixpanelInit = usePersistentCallback(async () => {
    if (!isMixPanelEnabled) return;
    if (!state.isUserIdentified && !state.isMixPanelLoaded) {
      try {
        await setDistinctID();
      } catch (error) {
        console.error(error);
      }
    }
  });

  const publicInitMixpanel = usePersistentCallback(async () => {
    if (!isMixPanelEnabled) return;
    try {
      if (!state.isMixPanelLoaded) {
        await initMixpanel();
        await track(EntityEvents[Entity.User].home.nonLoggedinUserFirstEvent);
      }
    } catch (error) {
      console.error(error);
    }
  });

  const track = usePersistentCallback(
    async (eventName: string, props: { [key: string]: unknown } = {}, event_category?: string) => {
      if (state.enabled) {
        if (event_category) {
          props['event_category'] = event_category;
        }
        const { userId, userEmail } = getUserDetails();
        const tab_session_id = getSessionValue();
        let trackProps = { ...props, tab_session_id } as { [key: string]: unknown };
        if (userId) {
          trackProps.email = userEmail;
          trackProps.userId = userId;
        }
        mixpanel.track(eventName, {
          event_data: trackProps,
          contexts: { ...state.contexts }
        });
        //Track event in full story
        fullStoryEvent(eventName, {
          event_data: trackProps,
          contexts: { ...state.contexts }
        });
        addBreadcrumb({
          category: SentryBreadcrumb.Mixpanel,
          message: eventName,
          level: 'info'
        });
      }
    }
  );

  const setDistinctID = usePersistentCallback(async (currentEmail?: string) => {
    const { userEmail, userId } = getUserDetails(currentEmail);
    if (isMixPanelEnabled) {
      await initMixpanel(userEmail);
      await track(EntityEvents[Entity.User].home.loggedInUserFirstEvent, {
        userId: userId,
        email: userEmail,
        source: currentUserData?.type
      });
    }
    const tab_session_id = getSessionValue();
    if (userEmail) {
      mixpanel.identify(userEmail);
      mixpanel.people.set({
        $email: userEmail,
        $userId: userId,
        $last_seen: new Date().toISOString(),
        $tab_session_id: tab_session_id,
        $time: getCurrentTimeStamp(),
        $insert_id: nanoid(10)
      });

      //TODO remove after ff poc
      identify(userEmail, {
        displayName: userEmail,
        email: userEmail,
        tab_session_id,
        useID: userId
      });
      setState((draft) => {
        draft.isUserIdentified = true;
      });
    }
  });

  const setOnceEvent = usePersistentCallback(async (props: { [key: string]: string | undefined }) => {
    if (state.enabled) {
      //Check if user is not identified just before setting event
      if (!state.isUserIdentified) {
        await setDistinctID(props?.email || currentUserData?.email);
      }
      mixpanel.people.set_once(props);
    }
  });

  const identifyAndSetPeople = usePersistentCallback(
    async (
      email: string,
      obj: {
        firstName: string;
        lastName?: string;
        source?: UserTypes;
        invitation?: string;
        userId?: string;
        company?: string;
        signup_date?: string;
      }
    ) => {
      if (state.enabled) {
        obj = obj || {};
        const firstName = obj.firstName;
        const lastName = obj.lastName && obj.lastName !== '--' ? obj.lastName : '';
        const { userId, userEmail, userFirstName, userLastName } = getUserDetails(email, firstName, lastName);
        //Identify user if not identified or override the user detail again if something name or anything else changed
        await setDistinctID(userEmail);

        const name =
          userFirstName || userLastName
            ? `${userFirstName ? userFirstName : ''} ${userLastName}`
            : getNameFromEmail(userEmail);
        const invitation = obj?.invitation;
        const tab_session_id = getSessionValue();
        const peopleObj: { [key: string]: string | undefined } = {
          $name: name,
          $email: userEmail,
          id: userEmail,
          $company: obj.company,
          $userId: userId,
          $firstName: userFirstName,
          $lastName: userLastName,
          $signup_date: obj.signup_date,
          $source: obj.source || currentUserData?.type,
          $last_seen: new Date().toISOString(),
          $tab_session_id: tab_session_id,
          $time: getCurrentTimeStamp(),
          $insert_id: nanoid(10)
        };

        if (invitation) {
          peopleObj.$invitation = invitation;
        }
        setUserVars(peopleObj);
        mixpanel.people.set(peopleObj);
        setUser({ email, username: name, ...obj, lastName: userLastName, uid: obj.userId });
      }
    }
  );

  return useMemo(
    () => ({
      publicInitMixpanel,
      setOnceEvent,
      track,
      loadFullStory,
      privateMixpanelInit,
      identifyAndSetPeople,
      addContext,
      removeContext
    }),
    [
      privateMixpanelInit,
      publicInitMixpanel,
      loadFullStory,
      track,
      identifyAndSetPeople,
      setOnceEvent,
      addContext,
      removeContext
    ]
  );
};
